import { useState, useEffect, useRef } from "react";
import "../../styles/toc.css";

/**
 * TableOfContents — Reusable sticky sidebar TOC
 *
 * Props:
 *   sections: Array<{ id: string, label: string }>
 *     id    — the section's HTML element id to observe
 *     label — the title shown on hover
 *
 * Usage:
 *   <TableOfContents sections={[
 *     { id: "hero",       label: "Introduction" },
 *     { id: "database",   label: "Exercise Database" },
 *   ]} />
 *
 * The component uses IntersectionObserver to track which section
 * is currently in view and highlights the corresponding TOC item.
 * Clicking an item smoothly scrolls to that section.
 */
export default function TableOfContents({ sections = [] }) {
  const [activeId, setActiveId] = useState(sections[0]?.id ?? "");
  const observerRef = useRef(null);

  useEffect(() => {
    // Clean up previous observer
    if (observerRef.current) observerRef.current.disconnect();

    const headerHeight = parseInt(
      getComputedStyle(document.documentElement).getPropertyValue("--header-height") || "80"
    );

    observerRef.current = new IntersectionObserver(
      (entries) => {
        // Pick the entry that is most visible and intersecting
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);

        if (visible.length > 0) {
          setActiveId(visible[0].target.id);
        }
      },
      {
        rootMargin: `-${headerHeight }px 0px -50% 0px`,
        threshold: [0, 0.1, 0.25, 0.5],
      }
    );

    sections.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observerRef.current.observe(el);
    });

    return () => observerRef.current?.disconnect();
  }, [sections]);

  const handleClick = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    const headerHeight = parseInt(
      getComputedStyle(document.documentElement).getPropertyValue("--header-height") || "80"
    );
    const top = el.getBoundingClientRect().top + window.scrollY - headerHeight - 24;
    window.scrollTo({ top, behavior: "smooth" });
  };

  return (
    <nav className="toc-wrapper" aria-label="Page sections">
      <div className="toc-inner">
        {sections.map(({ id, label }) => (
          <button
            key={id}
            className={`toc-item${activeId === id ? " toc-active" : ""}`}
            onClick={() => handleClick(id)}
            aria-label={`Jump to ${label}`}
            title={label}
          >
            <span className="toc-line" aria-hidden="true" />
            <span className="toc-label">{label}</span>
          </button>
        ))}
      </div>
    </nav>
  );
}
